import { z } from "zod";
import { FieldMetaMap, SchemaConfig, WidgetType } from "./types";

/**
 * Given a SchemaConfig and the current discriminator value, return the
 * active ZodObject schema for that variant (for introspection).
 */
export function getActiveSchema(
  config: SchemaConfig,
  discriminatorValue: string | undefined,
): z.ZodObject<z.ZodRawShape> {
  if (discriminatorValue && config.variants[discriminatorValue]) {
    return config.variants[discriminatorValue];
  }
  if (config.defaultVariantKey && config.variants[config.defaultVariantKey]) {
    return config.variants[config.defaultVariantKey];
  }
  return config.base;
}

/**
 * Given a SchemaConfig and the current discriminator value, return the
 * active validator (for validation).  Falls back to the variant ZodObject
 * if no dedicated validator is configured.
 */
export function getActiveValidator(
  config: SchemaConfig,
  discriminatorValue: string | undefined,
): z.ZodTypeAny {
  if (discriminatorValue && config.validators?.[discriminatorValue]) {
    return config.validators[discriminatorValue];
  }
  // Fall back to the shape (ZodObject) for validation
  return getActiveSchema(config, discriminatorValue);
}

/**
 * Extract the set of field names from a ZodObject schema.
 */
export function getSchemaFieldNames(
  schema: z.ZodObject<z.ZodRawShape>,
): Set<string> {
  return new Set(Object.keys(schema.shape));
}

/**
 * Determine if a field is required (non-optional) in a ZodObject schema.
 */
export function isFieldRequired(
  schema: z.ZodObject<z.ZodRawShape>,
  fieldName: string,
): boolean {
  const shape = schema.shape;
  const fieldSchema = shape[fieldName];
  if (!fieldSchema) return false;
  return !isZodOptional(fieldSchema);
}

/**
 * Walk a Zod type to check if it's optional at the top level.
 */
function isZodOptional(schema: z.ZodTypeAny): boolean {
  if (schema instanceof z.ZodOptional) return true;
  if (schema instanceof z.ZodNullable) return true;
  // ZodDefault wraps an inner type — the field is always provided so treat as not-optional
  if (schema instanceof z.ZodDefault) return false;
  return false;
}

/**
 * Given a set of values and a new active schema, strip out (set to undefined)
 * any fields that are NOT in the new schema's shape.  Fields in the base
 * schema are always kept.
 *
 * This is the "auto-clear" mechanism: when the discriminator changes,
 * variant-specific fields from the old variant get wiped.
 */
export function stripFieldsNotInSchema<T extends Record<string, unknown>>(
  values: T,
  newSchema: z.ZodObject<z.ZodRawShape>,
  baseSchema: z.ZodObject<z.ZodRawShape>,
): T {
  const newFields = getSchemaFieldNames(newSchema);
  const baseFields = getSchemaFieldNames(baseSchema);

  const result = { ...values };
  for (const key of Object.keys(result)) {
    // Keep everything that's in the new schema OR in the base schema
    if (newFields.has(key) || baseFields.has(key)) continue;
    (result as Record<string, unknown>)[key] = undefined;
  }
  return result;
}

/**
 * Infer a sensible widget type from a Zod field schema.
 */
export function inferWidgetType(schema: z.ZodTypeAny): WidgetType {
  const unwrapped = unwrapZodType(schema);

  if (unwrapped instanceof z.ZodNumber) {
    return "int";
  }
  if (unwrapped instanceof z.ZodBoolean) {
    return "checkbox";
  }
  if (unwrapped instanceof z.ZodEnum || unwrapped instanceof z.ZodLiteral) {
    return "select";
  }
  // Default to text for strings and everything else
  return "text";
}

/**
 * Unwrap ZodOptional, ZodNullable, ZodDefault to get the inner type.
 */
function unwrapZodType(schema: z.ZodTypeAny): z.ZodTypeAny {
  if (schema instanceof z.ZodOptional) return unwrapZodType(schema.unwrap());
  if (schema instanceof z.ZodNullable) return unwrapZodType(schema.unwrap());
  if (schema instanceof z.ZodDefault) return unwrapZodType(schema._def.innerType);
  return schema;
}

/**
 * Build a copy of `data` where any field that has a `serializeForValidation`
 * function in its meta gets transformed before Zod sees it.
 *
 * This lets the form store e.g. `Date` objects internally while the Zod
 * schema expects `string`.
 */
export function serializeForValidation<T extends Record<string, unknown>>(
  data: T,
  fieldMeta: FieldMetaMap<Record<string, unknown>>,
): Record<string, unknown> {
  const out: Record<string, unknown> = { ...data };
  for (const [key, meta] of Object.entries(fieldMeta)) {
    if (meta.serializeForValidation && out[key] !== undefined && out[key] !== null) {
      out[key] = meta.serializeForValidation(out[key]);
    }
  }
  return out;
}

/**
 * Run validation and extract per-field error messages.
 *
 * Accepts any ZodTypeAny (ZodObject, ZodEffects, etc.) so it works
 * with schemas that have .superRefine() / .refine().
 *
 * When `fieldMeta` is provided, field values are serialized (via
 * `serializeForValidation`) before being passed to Zod.
 */
export function validateSchema<T extends Record<string, unknown>>(
  schema: z.ZodTypeAny,
  data: T,
  fieldMeta?: FieldMetaMap<Record<string, unknown>>,
): { success: boolean; data?: T; errors?: Record<string, string> } {
  const toValidate = fieldMeta ? serializeForValidation(data, fieldMeta) : data;
  const result = schema.safeParse(toValidate);
  if (result.success) {
    return { success: true, data: result.data as T };
  }

  const errors: Record<string, string> = {};
  for (const issue of result.error.issues) {
    const path = issue.path.join(".");
    if (path && !errors[path]) {
      errors[path] = issue.message;
    }
  }
  return { success: false, errors };
}
